/**
 * TGE Resource Center V2
 * - Topic links are real /tagged/ URLs; clicks swap the hero, sidebar and grid in place
 *   through the Section Rendering API and keep browser history in sync.
 * - Sidebar search shows suggested searches, matching topics and articles while typing,
 *   and full Resource Center results on Enter, without leaving the page.
 * - Events from the search box are stopped at the window capture phase, so site-wide
 *   search apps (Boost AI Search) never see or override typing in it.
 */
(() => {
  const RESULTS_SECTION = 'tge-rc-search-results';
  const MIN_CHARS = 2;
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const STOP_WORDS = new Set(['and', 'the', 'your', 'for', 'with', 'how', 'you', 'our', 'from', 'into', 'that', 'this']);

  const ICON_SEARCH =
    '<svg class="rc-suggest__icon" viewBox="0 0 20 20" aria-hidden="true"><circle cx="8.5" cy="8.5" r="5.5" fill="none" stroke="currentColor" stroke-width="2"/><path d="m13 13 4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
  const ICON_ARROW =
    '<svg class="rc-suggest__icon" viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h12m-5-5 5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  let navToken = 0;

  const getHub = () => document.querySelector('[data-rc-hub]');
  const hubOf = (el) => el.closest('[data-rc-hub]');

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const escapeHtml = (value) =>
    String(value == null ? '' : value).replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
    );

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const addWords = (set, text) =>
    normalize(text)
      .split(' ')
      .forEach((word) => {
        if (word.length > 2 && !STOP_WORDS.has(word)) set.add(word);
      });

  function highlight(text, query) {
    const source = String(text || '');
    const words = normalize(query).split(' ').filter(Boolean);
    if (!words.length) return escapeHtml(source);
    const pattern = new RegExp('\\b(' + words.map(escapeRegExp).join('|') + ')', 'gi');
    let out = '';
    let last = 0;
    source.replace(pattern, (match, _group, index) => {
      out += escapeHtml(source.slice(last, index)) + '<mark>' + escapeHtml(match) + '</mark>';
      last = index + match.length;
      return match;
    });
    return out + escapeHtml(source.slice(last));
  }

  function state(hub) {
    if (hub._rc) return hub._rc;

    let topics = [];
    try {
      topics = JSON.parse(hub.querySelector('[data-rc-topics-data]').textContent) || [];
    } catch (error) {
      topics = [];
    }

    // keywords: topic title -> its keywords (for topic matching); phrases: search terms we can suggest.
    const keywords = {};
    const phrases = new Set();
    try {
      const raw = JSON.parse(hub.querySelector('[data-rc-keywords]').textContent) || '';
      raw.split(/\r?\n/).forEach((line) => {
        const split = line.indexOf(':');
        if (split < 1) return;
        const list = line.slice(split + 1).split(',').map(normalize).filter(Boolean);
        keywords[normalize(line.slice(0, split))] = list.join(' ');
        list.forEach((phrase) => phrases.add(phrase));
      });
    } catch (error) {
      // Keywords are optional.
    }

    let tags = [];
    try {
      tags = JSON.parse(hub.querySelector('[data-rc-tags]').textContent) || [];
    } catch (error) {
      tags = [];
    }

    // Words that belong to the Resource Center, used to drop store-wide product suggestions.
    // Tags only feed this vocabulary; they are not suggested directly (they repeat topics and include typos).
    const vocab = new Set();
    topics.forEach((topic) => addWords(vocab, topic.title));
    phrases.forEach((phrase) => addWords(vocab, phrase));
    tags.forEach((tag) => addWords(vocab, tag));

    hub._rc = {
      blogPath: new URL(hub.dataset.blogUrl, location.origin).pathname.replace(/\/$/, ''),
      sectionId: hub.dataset.sectionId,
      viewQ: hub.dataset.viewQ || '',
      topics,
      keywords,
      phrases: Array.from(phrases).filter(Boolean),
      vocab,
      active: -1,
      timer: null,
      controller: null,
    };
    return hub._rc;
  }

  const els = (hub) => ({
    input: hub.querySelector('[data-rc-input]'),
    box: hub.querySelector('[data-rc-suggest]'),
    clear: hub.querySelector('[data-rc-clear]'),
    search: hub.querySelector('[data-rc-search]'),
  });

  /* ---------- URLs ---------- */

  function isListing(url, st) {
    return url.pathname === st.blogPath || url.pathname.indexOf(st.blogPath + '/tagged/') === 0;
  }

  function isArticle(url, st) {
    return url.pathname.indexOf(st.blogPath + '/') === 0 && !isListing(url, st);
  }

  function withView(url, st) {
    if (!st.viewQ) return url;
    const [key, value] = st.viewQ.split('=');
    if (key && value && !url.searchParams.has(key)) url.searchParams.set(key, value);
    return url;
  }

  // Section Rendering responses carry section_id in their own links; while previewing
  // an alternate template, listing links also need ?view= so reviewers stay on V2.
  function tidyLinks(root, st) {
    root.querySelectorAll('a[href]').forEach((link) => {
      let url;
      try {
        url = new URL(link.getAttribute('href'), location.origin);
      } catch (error) {
        return;
      }
      if (url.origin !== location.origin) return;
      url.searchParams.delete('section_id');
      if (isListing(url, st) || isArticle(url, st)) withView(url, st);
      link.setAttribute('href', url.pathname + url.search + url.hash);
    });
  }

  /* ---------- Layout helpers ---------- */

  function stickyOffset() {
    let bottom = 0;
    document
      .querySelectorAll('.shopify-section-group-header-group, [data-section-type="static-header"], .site-header')
      .forEach((el) => {
        const position = getComputedStyle(el).position;
        if (position !== 'sticky' && position !== 'fixed') return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= 1 && rect.bottom > bottom) bottom = rect.bottom;
      });
    return bottom;
  }

  function updateSticky(hub) {
    hub.style.setProperty('--rc-sticky-top', Math.round(stickyOffset() + 20) + 'px');
  }

  function scrollIntoViewTop(el) {
    if (!el) return;
    const offset = stickyOffset() + 12;
    const rect = el.getBoundingClientRect();
    if (rect.top >= offset && rect.top <= window.innerHeight * 0.6) return;
    window.scrollTo({ top: rect.top + window.scrollY - offset, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
  }

  function setLoading(hub, on) {
    hub.classList.toggle('is-loading', on);
    hub.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  function setCurrentLabel(hub, label) {
    const el = hub.querySelector('[data-rc-current]');
    if (el && label) el.textContent = label;
  }

  function toggleTopics(hub, force) {
    const button = hub.querySelector('[data-rc-topics-toggle]');
    const nav = hub.querySelector('[data-rc-topics]');
    if (!button || !nav) return;
    const open = typeof force === 'boolean' ? force : button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(open));
    nav.classList.toggle('is-open', open);
  }

  /* ---------- Topic navigation ---------- */

  function swap(hub, doc) {
    const openTopics = Array.from(hub.querySelectorAll('.rc-topic[open]')).map((el) => el.dataset.parent);

    ['[data-rc-hero]', '[data-rc-topics]', '[data-rc-main]'].forEach((selector) => {
      const next = doc.querySelector(selector);
      const current = hub.querySelector(selector);
      if (next && current) current.replaceWith(next);
    });

    hub.querySelectorAll('.rc-topic').forEach((el) => {
      if (openTopics.indexOf(el.dataset.parent) !== -1) el.open = true;
    });

    tidyLinks(hub, state(hub));

    const main = hub.querySelector('[data-rc-main]');
    if (main) {
      if (main.dataset.pageTitle) document.title = main.dataset.pageTitle;
      setCurrentLabel(hub, main.dataset.currentLabel);
    }
    toggleTopics(hub, false);
  }

  async function navigate(hub, href, { push = true } = {}) {
    const st = state(hub);
    const page = withView(new URL(href, location.origin), st);
    page.searchParams.delete('section_id');
    page.searchParams.delete('q');

    const request = new URL(page.href);
    request.searchParams.set('section_id', st.sectionId);

    const token = ++navToken;
    setLoading(hub, true);
    try {
      const response = await fetch(request.href, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const html = await response.text();
      if (token !== navToken) return;

      swap(hub, new DOMParser().parseFromString(html, 'text/html'));
      hub.classList.remove('is-searching');
      if (push) history.pushState({ rc: true }, '', page.pathname + page.search + page.hash);
      scrollIntoViewTop(hub.querySelector('[data-rc-hero]'));
    } catch (error) {
      if (token === navToken) window.location.assign(page.href);
    } finally {
      if (token === navToken) setLoading(hub, false);
    }
  }

  /* ---------- Suggestions ---------- */

  function matchTopics(st, query) {
    const words = normalize(query)
      .split(' ')
      .filter((word) => word.length > 1);
    if (!words.length) return [];

    return st.topics
      .map((topic) => {
        const titleWords = normalize(topic.title).split(' ');
        const extraWords = normalize((topic.parent || '') + ' ' + (st.keywords[normalize(topic.title)] || '')).split(' ');
        let score = 0;
        for (const word of words) {
          if (titleWords.some((w) => w.indexOf(word) === 0)) score += 2;
          else if (extraWords.some((w) => w.indexOf(word) === 0)) score += 1;
          else return null;
        }
        if (topic.parent) score += 0.5;
        return { topic, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((match) => match.topic);
  }

  function articleImage(article) {
    const src = article.image || (article.featured_image && article.featured_image.url);
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=160';
  }

  const group = (label, items) =>
    '<div class="rc-suggest__group" role="group" aria-label="' +
    escapeHtml(label) +
    '"><p class="rc-suggest__label" aria-hidden="true">' +
    escapeHtml(label) +
    '</p>' +
    items +
    '</div>';

  function renderSuggest(hub, query, { topics = [], queries = [], articles = [], loading = false }) {
    const { box, input } = els(hub);
    const st = state(hub);
    let index = 0;
    const nextId = () => 'rc-opt-' + index++;
    const parts = [];

    if (queries.length) {
      parts.push(
        group(
          'Suggested searches',
          queries
            .map(
              (text) =>
                '<button type="button" class="rc-suggest__item" role="option" id="' +
                nextId() +
                '" data-rc-query="' +
                escapeHtml(text) +
                '">' +
                ICON_SEARCH +
                '<span class="rc-suggest__text">' +
                highlight(text, query) +
                '</span></button>'
            )
            .join('')
        )
      );
    }

    if (topics.length) {
      parts.push(
        group(
          'Topics',
          topics
            .map(
              (topic) =>
                '<a class="rc-suggest__item" role="option" id="' +
                nextId() +
                '" href="' +
                escapeHtml(topic.url) +
                '" data-rc-nav data-parent="' +
                escapeHtml(topic.key) +
                '"><span class="rc-suggest__dot" aria-hidden="true"></span><span class="rc-suggest__text">' +
                highlight(topic.title, query) +
                '</span>' +
                (topic.parent ? '<span class="rc-suggest__meta">' + escapeHtml(topic.parent) + '</span>' : '') +
                '</a>'
            )
            .join('')
        )
      );
    }

    if (articles.length) {
      parts.push(
        group(
          'Resources',
          articles
            .map((article) => {
              const image = articleImage(article);
              return (
                '<a class="rc-suggest__item" role="option" id="' +
                nextId() +
                '" href="' +
                escapeHtml(article.url) +
                '">' +
                (image ? '<img class="rc-suggest__thumb" src="' + escapeHtml(image) + '" alt="" loading="lazy">' : '') +
                '<span class="rc-suggest__text rc-suggest__title">' +
                highlight(article.title, query) +
                '</span></a>'
              );
            })
            .join('')
        )
      );
    }

    if (loading) {
      parts.push('<p class="rc-suggest__status" role="status">Finding suggestions…</p>');
    } else if (!queries.length && !topics.length && !articles.length) {
      parts.push(
        '<p class="rc-suggest__status" role="status">No quick matches for “' +
          escapeHtml(query) +
          '”. Press Enter to search every resource.</p>'
      );
    }

    parts.push(
      '<div class="rc-suggest__footer"><button type="button" class="rc-suggest__item rc-suggest__all" role="option" id="' +
        nextId() +
        '" data-rc-query="' +
        escapeHtml(query) +
        '"><span>See all results for “' +
        escapeHtml(query) +
        '”</span>' +
        ICON_ARROW +
        '</button></div>'
    );

    box.innerHTML = parts.join('');
    box.hidden = false;
    input.setAttribute('aria-expanded', 'true');

    const options = getOptions(hub);
    if (st.active >= options.length) st.active = -1;
    if (st.active >= 0) setActive(hub, st.active);
    else input.removeAttribute('aria-activedescendant');
  }

  function closeSuggest(hub) {
    const { box, input } = els(hub);
    const st = state(hub);
    clearTimeout(st.timer);
    if (st.controller) st.controller.abort();
    st.active = -1;
    if (!box) return;
    box.hidden = true;
    box.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }

  async function fetchPredictive(query, signal) {
    const url = new URL(ROOT + 'search/suggest.json', location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'query,article');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,tag,body');
    const response = await fetch(url.href, { signal, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('HTTP ' + response.status);
    const data = await response.json();
    return (data.resources && data.resources.results) || {};
  }

  // Suggested searches: Resource Center keywords and tags that match what was typed, then
  // Shopify's store-wide query suggestions, kept only when every word belongs to the
  // Resource Center vocabulary (so product terms like "treadmill" never show here).
  function suggestedQueries(st, query, results) {
    const typed = normalize(query);
    const words = typed.split(' ');
    const last = words[words.length - 1];

    const vocab = new Set(st.vocab);
    (results.articles || []).forEach((article) => {
      if (article.url && isArticle(new URL(article.url, location.origin), st)) addWords(vocab, article.title);
    });
    const known = (word) => {
      for (const known of vocab) {
        if (known.indexOf(word) === 0 || word.indexOf(known) === 0) return true;
      }
      return false;
    };

    const local = st.phrases
      .filter(
        (phrase) =>
          phrase !== typed &&
          (phrase.indexOf(typed) === 0 || (last.length > 1 && phrase.split(' ').some((w) => w.indexOf(last) === 0)))
      )
      .sort((a, b) => (b.indexOf(typed) === 0) - (a.indexOf(typed) === 0) || a.length - b.length)
      .slice(0, 3);

    const store = (results.queries || [])
      .map((item) => normalize(item.text))
      .filter((text) => text && text.split(' ').every((w) => w.length < 3 || STOP_WORDS.has(w) || known(w)));

    const seen = new Set([typed]);
    return local
      .concat(store)
      .filter((text) => !seen.has(text) && seen.add(text))
      .slice(0, 5);
  }

  function suggest(hub, raw) {
    const st = state(hub);
    const query = String(raw || '').trim();
    clearTimeout(st.timer);
    if (st.controller) st.controller.abort();

    if (query.length < MIN_CHARS) {
      closeSuggest(hub);
      return;
    }

    const topics = matchTopics(st, query);
    renderSuggest(hub, query, { topics, queries: suggestedQueries(st, query, {}), loading: true });

    st.timer = setTimeout(async () => {
      st.controller = new AbortController();
      try {
        const results = await fetchPredictive(query, st.controller.signal);
        if (els(hub).input.value.trim() !== query) return;
        const articles = (results.articles || [])
          .filter((article) => article.url && isArticle(new URL(article.url, location.origin), st))
          .slice(0, 4);
        renderSuggest(hub, query, { topics, queries: suggestedQueries(st, query, results), articles });
      } catch (error) {
        if (error.name !== 'AbortError') renderSuggest(hub, query, { topics, queries: suggestedQueries(st, query, {}) });
      }
    }, 180);
  }

  const getOptions = (hub) => Array.from(els(hub).box.querySelectorAll('[role="option"]'));

  function setActive(hub, index) {
    const st = state(hub);
    const options = getOptions(hub);
    const { input } = els(hub);
    if (!options.length) return;
    st.active = ((index % options.length) + options.length) % options.length;
    options.forEach((option, n) => {
      const on = n === st.active;
      option.classList.toggle('is-active', on);
      option.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    input.setAttribute('aria-activedescendant', options[st.active].id);
    options[st.active].scrollIntoView({ block: 'nearest' });
  }

  function activate(hub, option) {
    if (option.hasAttribute('data-rc-query')) runSearch(hub, option.getAttribute('data-rc-query'), { push: true });
    else option.click();
  }

  function syncClear(hub) {
    const { input, clear } = els(hub);
    if (clear) clear.hidden = !input.value;
  }

  /* ---------- Full results ---------- */

  async function fillEmptySuggestions(hub, query, container) {
    const target = container.querySelector('[data-rc-empty-suggest]');
    if (!target) return;
    try {
      const queries = suggestedQueries(state(hub), query, await fetchPredictive(query));
      if (!queries.length) return;
      target.innerHTML =
        '<p class="rc-results__try">Try one of these searches:</p><div class="rc-chips rc-chips--static">' +
        queries
          .map(
            (text) =>
              '<button type="button" class="rc-chip" data-rc-query="' + escapeHtml(text) + '">' + escapeHtml(text) + '</button>'
          )
          .join('') +
        '</div>';
    } catch (error) {
      // Topic links in the empty state still give a way forward.
    }
  }

  async function runSearch(hub, raw, { push = true } = {}) {
    const st = state(hub);
    const query = String(raw || '').trim();
    const { input } = els(hub);
    if (!query) return;

    closeSuggest(hub);
    input.value = query;
    syncClear(hub);

    // Guide pages have no results area: continue the search on the Resource Center page.
    if (!hub.querySelector('[data-rc-main]')) {
      const page = withView(new URL(hub.dataset.blogUrl, location.origin), st);
      page.searchParams.set('q', query);
      window.location.assign(page.href);
      return;
    }

    const request = new URL(ROOT + 'search', location.origin);
    request.searchParams.set('q', query);
    request.searchParams.set('type', 'article');
    request.searchParams.set('options[prefix]', 'last');
    request.searchParams.set('section_id', RESULTS_SECTION);

    const token = ++navToken;
    setLoading(hub, true);
    try {
      const response = await fetch(request.href, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (token !== navToken) return;

      const results = doc.querySelector('[data-rc-results]');
      const main = hub.querySelector('[data-rc-main]');
      if (!results || !main) throw new Error('Missing results markup');

      main.replaceChildren(results);
      tidyLinks(main, st);
      hub.classList.add('is-searching');
      setCurrentLabel(hub, 'Search results');
      document.title = 'Search: ' + query + ' – Resource Center';

      if (push) {
        const page = new URL(location.href);
        page.searchParams.set('q', query);
        page.searchParams.delete('page');
        history.pushState({ rc: true }, '', page.pathname + page.search);
      }

      if (results.getAttribute('data-count') === '0') fillEmptySuggestions(hub, query, results);
      scrollIntoViewTop(main);
    } catch (error) {
      if (token === navToken) {
        const fallback = new URL(ROOT + 'search', location.origin);
        fallback.searchParams.set('q', query);
        fallback.searchParams.set('type', 'article');
        window.location.assign(fallback.href);
      }
    } finally {
      if (token === navToken) setLoading(hub, false);
    }
  }

  function clearSearch(hub) {
    const { input } = els(hub);
    input.value = '';
    syncClear(hub);
    closeSuggest(hub);
    if (hub.classList.contains('is-searching')) {
      const page = new URL(location.href);
      page.searchParams.delete('q');
      navigate(hub, page.href, { push: true });
    }
  }

  /* ---------- Quick view ---------- */

  const VIDEO_ID = /^[A-Za-z0-9_-]{6,20}$/;
  const quickCards = (scope) => Array.from(scope.querySelectorAll('.rc-card[data-rc-video]'));

  const youtubeFrame = (id, title) =>
    '<iframe src="https://www.youtube-nocookie.com/embed/' +
    id +
    '?autoplay=1&rel=0&modestbranding=1&playsinline=1" title="' +
    escapeHtml(title) +
    '" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>';

  // Quick view steps through the cards of the grid the opened card belongs to.
  function openQuickView(hub, card) {
    const dialog = hub.querySelector('[data-rc-qv]');
    const id = card && card.getAttribute('data-rc-video');
    if (!dialog || typeof dialog.showModal !== 'function' || !VIDEO_ID.test(id || '')) return false;

    const find = (selector) => dialog.querySelector(selector);
    const link = card.querySelector('.rc-card__link');
    const badge = card.querySelector('.rc-card__badge');
    const title = link ? link.textContent.trim() : '';

    find('[data-rc-qv-video]').innerHTML = youtubeFrame(id, title);
    find('[data-rc-qv-title]').textContent = title;
    find('[data-rc-qv-summary]').textContent = card.getAttribute('data-rc-summary') || '';
    find('[data-rc-qv-link]').setAttribute('href', link ? link.getAttribute('href') : '#');

    const quickBadge = find('[data-rc-qv-badge]');
    quickBadge.hidden = !badge;
    if (badge) {
      quickBadge.textContent = badge.textContent.trim();
      quickBadge.setAttribute('href', badge.getAttribute('href'));
    }
    if (card.dataset.parent) quickBadge.setAttribute('data-parent', card.dataset.parent);
    else quickBadge.removeAttribute('data-parent');

    const scope = card.closest('.rc-grid') || hub;
    const cards = quickCards(scope);
    const index = cards.indexOf(card);
    dialog._rcScope = scope;
    dialog._rcIndex = index;
    find('[data-rc-qv-count]').textContent = cards.length > 1 ? index + 1 + ' of ' + cards.length : '';
    find('[data-rc-qv-prev]').disabled = index <= 0;
    find('[data-rc-qv-next]').disabled = index === -1 || index >= cards.length - 1;

    dialog._rcReturn = card.querySelector('[data-rc-quick]');
    if (!dialog.open) {
      dialog.showModal();
      document.documentElement.classList.add('rc-qv-open');
    }
    return true;
  }

  function stepQuickView(hub, delta) {
    const dialog = hub.querySelector('[data-rc-qv]');
    const next = quickCards(dialog._rcScope || hub)[(dialog._rcIndex || 0) + delta];
    if (next) openQuickView(hub, next);
  }

  function closeQuickView(hub) {
    const dialog = hub.querySelector('[data-rc-qv]');
    if (dialog && dialog.open) dialog.close();
  }

  function bindQuickView(hub) {
    const dialog = hub.querySelector('[data-rc-qv]');
    if (!dialog) return;

    // Stop the video, unlock scrolling and return focus to the card that opened it.
    dialog.addEventListener('close', () => {
      dialog.querySelector('[data-rc-qv-video]').innerHTML = '';
      document.documentElement.classList.remove('rc-qv-open');
      if (dialog._rcReturn && document.contains(dialog._rcReturn)) dialog._rcReturn.focus();
    });

    // A click that lands on the dialog element itself is a click on the backdrop.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        stepQuickView(hub, 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        stepQuickView(hub, -1);
      }
    });
  }

  /* ---------- Keyboard ---------- */

  function onKeydown(event, hub) {
    const st = state(hub);
    const { input, box } = els(hub);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (box.hidden) suggest(hub, input.value);
        else setActive(hub, st.active + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!box.hidden) setActive(hub, st.active <= 0 ? getOptions(hub).length - 1 : st.active - 1);
        break;
      case 'Enter': {
        event.preventDefault();
        const option = !box.hidden && st.active >= 0 ? getOptions(hub)[st.active] : null;
        if (option) activate(hub, option);
        else runSearch(hub, input.value, { push: true });
        break;
      }
      case 'Escape':
        if (!box.hidden) {
          event.preventDefault();
          closeSuggest(hub);
        } else if (input.value) {
          event.preventDefault();
          input.value = '';
          syncClear(hub);
        }
        break;
      default:
        break;
    }
  }

  // Handle the search box from the window capture phase and stop propagation there, so
  // document- and element-level listeners added by other scripts never receive its events.
  const inputHandlers = {
    input(event, input) {
      const hub = hubOf(input);
      syncClear(hub);
      suggest(hub, input.value);
    },
    keydown(event, input) {
      onKeydown(event, hubOf(input));
    },
    focusin(event, input) {
      if (input.value.trim().length >= MIN_CHARS) suggest(hubOf(input), input.value);
    },
    focusout(event, input) {
      const hub = hubOf(input);
      setTimeout(() => {
        const { search } = els(hub);
        if (search && !search.contains(document.activeElement)) closeSuggest(hub);
      }, 150);
    },
  };

  [
    'keydown',
    'keypress',
    'keyup',
    'beforeinput',
    'input',
    'change',
    'paste',
    'compositionstart',
    'compositionend',
    'focusin',
    'focusout',
  ].forEach((type) => {
    window.addEventListener(
      type,
      (event) => {
        const target = event.target;
        const input = target && target.closest ? target.closest('[data-rc-input]') : null;
        if (!input) return;
        event.stopPropagation();
        if (inputHandlers[type]) inputHandlers[type](event, input);
      },
      true
    );
  });

  /* ---------- Clicks ---------- */

  // Keep focus in the search box while picking a suggestion (Safari does not focus buttons on click).
  document.addEventListener('mousedown', (event) => {
    if (event.target.closest && event.target.closest('[data-rc-suggest]')) event.preventDefault();
  });

  document.addEventListener('click', (event) => {
    const hub = getHub();
    if (!hub) return;
    const target = event.target;
    const { search } = els(hub);

    if (search && !search.contains(target)) closeSuggest(hub);
    if (!hub.contains(target)) return;

    const query = target.closest('[data-rc-query]');
    if (query) {
      event.preventDefault();
      runSearch(hub, query.getAttribute('data-rc-query'), { push: true });
      return;
    }

    if (target.closest('[data-rc-clear]')) {
      event.preventDefault();
      clearSearch(hub);
      els(hub).input.focus();
      return;
    }

    if (target.closest('[data-rc-clear-results]')) {
      event.preventDefault();
      clearSearch(hub);
      return;
    }

    if (target.closest('[data-rc-topics-toggle]')) {
      event.preventDefault();
      toggleTopics(hub);
      return;
    }

    const quick = target.closest('[data-rc-quick]');
    if (quick) {
      event.preventDefault();
      const card = quick.closest('.rc-card');
      if (!openQuickView(hub, card)) {
        const articleLink = card && card.querySelector('.rc-card__link');
        if (articleLink) window.location.assign(articleLink.href);
      }
      return;
    }

    if (target.closest('[data-rc-qv-close]')) {
      event.preventDefault();
      closeQuickView(hub);
      return;
    }

    if (target.closest('[data-rc-qv-prev], [data-rc-qv-next]')) {
      event.preventDefault();
      stepQuickView(hub, target.closest('[data-rc-qv-next]') ? 1 : -1);
      return;
    }

    // Guide page video: swap the poster for the player only when it's wanted.
    const play = target.closest('[data-rc-play]');
    if (play) {
      event.preventDefault();
      const id = play.getAttribute('data-rc-play');
      if (VIDEO_ID.test(id || '')) play.outerHTML = youtubeFrame(id, play.getAttribute('aria-label') || '');
      return;
    }

    const copy = target.closest('[data-rc-copy]');
    if (copy) {
      event.preventDefault();
      if (navigator.clipboard) {
        navigator.clipboard.writeText(copy.getAttribute('data-rc-copy')).then(
          () => {
            copy.textContent = copy.getAttribute('data-copied-label') || 'Copied';
          },
          () => {}
        );
      }
      return;
    }

    const link = target.closest('a[data-rc-nav]');
    if (!link) return;
    // Guide pages have no grid to swap, so their topic links navigate normally.
    if (!hub.querySelector('[data-rc-main]')) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin || !isListing(url, state(hub))) return;

    event.preventDefault();
    if (link.closest('[data-rc-qv]')) closeQuickView(hub);
    if (link.closest('[data-rc-suggest]')) {
      els(hub).input.value = '';
      syncClear(hub);
      closeSuggest(hub);
    }
    navigate(hub, url.href, { push: true });
  });

  window.addEventListener('popstate', () => {
    const hub = getHub();
    if (!hub) return;
    const url = new URL(location.href);
    const query = url.searchParams.get('q');
    if (query) {
      runSearch(hub, query, { push: false });
    } else if (isListing(url, state(hub))) {
      els(hub).input.value = '';
      syncClear(hub);
      navigate(hub, url.href, { push: false });
    }
  });

  let ticking = false;
  const onViewportChange = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      const hub = getHub();
      if (hub) updateSticky(hub);
    });
  };
  window.addEventListener('scroll', onViewportChange, { passive: true });
  window.addEventListener('resize', onViewportChange);

  /* ---------- Init ---------- */

  // Guide bodies are plain paragraphs; short lines ending in a colon ("Key Takeaways:") read as headings.
  function markGuideHeadings(hub) {
    hub.querySelectorAll('[data-rc-guide-body] p').forEach((paragraph) => {
      const text = paragraph.textContent.trim();
      if (text.length > 1 && text.length < 60 && text.slice(-1) === ':') paragraph.classList.add('rc-guide__subhead');
    });
  }

  function init() {
    const hub = getHub();
    if (!hub || hub._rcInit) return;
    hub._rcInit = true;
    const st = state(hub);
    bindQuickView(hub);
    tidyLinks(hub, st);
    markGuideHeadings(hub);
    history.replaceState(Object.assign({}, history.state, { rc: true }), '');
    updateSticky(hub);

    const query = new URL(location.href).searchParams.get('q');
    if (query && hub.querySelector('[data-rc-main]')) runSearch(hub, query, { push: false });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
